// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import berekenNoemer from './berekenNoemer'
import bepaalStatus from './bepaalStatus'
import rondAfOpVijfCent from './rondAfOpVijfCent'
import METHODIEK_VERSIE from './methodiekVersie'

// ToggleButton persisteert boolean-vlaggen als strings — beide vormen gelden.
// AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
const isBevestigd = (v) => v === true || v === 'true'

/**
 * @typedef {object} KengetalResultaat
 * @property {number|null} kengetal - Het presenteerbare kengetal (€ 0,05-floor, § 5.3), of null zonder geldige teller/noemer of bij status 'onvoldoende'
 * @property {number|null} ruweRatio - Onafgeronde teller ÷ noemer (voor de berekening-uitleg)
 * @property {'definitief'|'voorlopig'|'onvoldoende'} status
 * @property {string|null} reden - Uitleg bij 'onvoldoende'
 * @property {string|null} redenCode - Vaste code bij de reden (bepaalStatus), voor koppelingAdvies
 * @property {string} label - Volledig presentatielabel, bv. "Impact per euro v1.0 (definitief, 2025)"
 * @property {import('./berekenNoemer').NoemerResultaat|null} noemer - Opbouw van de noemer
 * @property {number|null} dekkingPct - Handmatig opgegeven dekkingspercentage (§ 5.1), of null
 * @property {number|null} kostenOpbrengstenVerhouding - Kosten ÷ opbrengsten (verplichte context, § 2.4), of null zonder inkomsten
 * @property {number} jaar - Het boekjaar van dit kengetal
 */

/**
 * Orkestreert het kengetal voor één boekjaar volgens de Impact per
 * euro-methodiek v1.0: teller (periode-gescopet, cohortprincipe § 1) ÷ noemer
 * (§ 2 + § 4), met status (§ 5.2) en presentatie-afronding (§ 5.3).
 *
 * @param {object} input
 * @param {number|null} input.teller - Boekjaar-gescopete maatschappelijke waarde (participatieladder-periode-delta + MSI-periode-delta); null zolang een stroom niet geladen is
 * @param {object|null} input.costsRow - De CompagnyCosts-rij van het boekjaar
 * @param {number|null} input.linkedMeasurementCount - Gekoppelde metingen in de periode (D5)
 * @param {Date} [input.now] - Peilmoment (injecteerbaar voor tests)
 * @returns {KengetalResultaat|null} null zonder kostenrij
 */
const computeImpactPerEuro = ({ teller, costsRow, linkedMeasurementCount, now = new Date() } = {}) => {
  if (!costsRow) return null

  const jaar = Number(costsRow.Year)

  const noemer = berekenNoemer({
    totalCosts: costsRow.TotalCosts,
    supportVolunteerHours: costsRow.SupportVolunteerHours,
    incidentalCosts: costsRow.IncidentalCosts,
    totalIncome: costsRow.TotalIncome,
    year: jaar,
  })

  const { status, reden, redenCode } = bepaalStatus({
    year: jaar,
    confirmed: isBevestigd(costsRow.Confirmed),
    linkedMeasurementCount,
    now,
  })

  const tellerGeldig = typeof teller === 'number' && Number.isFinite(teller)
  const ruweRatio = tellerGeldig && noemer ? teller / noemer.noemer : null
  const kengetal = ruweRatio !== null && status !== 'onvoldoende'
    ? rondAfOpVijfCent(ruweRatio)
    : null

  // Number(null) is 0 — een niet-ingevulde dekking mag niet als 0% doorgaan.
  const dekkingOpgegeven = costsRow.CoveragePct !== null && costsRow.CoveragePct !== undefined && costsRow.CoveragePct !== ''
  const dekking = Number(costsRow.CoveragePct)
  const kosten = Number(costsRow.TotalCosts)
  const inkomsten = Number(costsRow.TotalIncome)

  return {
    kengetal,
    ruweRatio,
    status,
    reden,
    redenCode,
    label: `Impact per euro v${METHODIEK_VERSIE} (${status}, ${jaar})`,
    noemer,
    dekkingPct: dekkingOpgegeven && Number.isFinite(dekking) ? dekking : null,
    kostenOpbrengstenVerhouding: Number.isFinite(kosten) && Number.isFinite(inkomsten) && inkomsten > 0
      ? kosten / inkomsten
      : null,
    jaar,
  }
}

export default computeImpactPerEuro
export { isBevestigd }
